import React = require('react');
import * as _ from 'underscore';

import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../codegen/Localize';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { Location } from '../../../common/react/Location';
import { IGroupDetailsCtrl } from '../../../ts/controllers/GroupDetailsCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { IGetGroupDetailInfoResult, IGroupInfo } from '../../../ts/models/IGroupInfo';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { reloadSpace } from '../../sagas/spaces/content/RefreshContentSagas';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { GroupSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { explorerSubspace } from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { GroupSpaceHeader } from './GroupSpaceHeader';

export interface GroupSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'group'>;
  readonly contentCountsUpToDate: boolean;
  readonly canSessionUserManageUsers: boolean;
}

export interface GroupSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
  readonly addSiteUsersToGroup?: (group: IGroupInfo) => void;
}

export interface GroupSpaceOwnProps extends ReactDirectiveProps {
  readonly urls: SpacesSiteUrls;
  readonly reloadSpace: () => void;
  readonly telemetry: Telemetry;
}
export type GroupSpaceProps = GroupSpaceStateProps & GroupSpaceOwnProps & GroupSpaceDispatchProps;

export class GroupSpace extends React.PureComponent<GroupSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getGroupSubspaces);

  public componentDidUpdate(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidMount(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public render() {
    const group = this.props.spaceInfo.item as IGroupInfo;
    const groupDetails = this.props.spaceInfo.item as IGetGroupDetailInfoResult;

    const subspaces = this.memoizedGetSiteUserSubspaces(
      this.props.angularContext,
      groupDetails,
      this.props.contentCountsUpToDate,
      this.navigateToAllGroups,
      reloadSpace,
      this.props.contentCounts,
      this.props.telemetry,
      group,
      this.props.canSessionUserManageUsers,
      this.props.addSiteUsersToGroup
    );

    return (
      <TabbedSpace
        subspaces = {subspaces}
        activeSubspaceName = {this.props.spaceInfo.subspaceName}
        urlForSubspace = {this.getGroupSubspaceUrl}
        mastheadElement = {this.mastheadElement()}
        testIdPrefix = {'groupspace'}
        spaceItemName = {groupDetails.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return <GroupSpaceHeader
      item={this.props.spaceInfo.item as IGroupInfo}
    />;
  }

  private navigateToAllGroups = () => {
    Location.instance.setUrl(this.props.urls.groupsUrl());
  }

  private static groupUrlBySubspace: Record<GroupSubspaceName, (urls: SpacesSiteUrls, groupId: string) => string> = {
    users: (urls, groupId) =>  urls.groupUsersUrl(groupId),
    details: (urls, groupId) =>  urls.groupDetailsUrl(groupId),
  };

  private getGroupSubspaceUrl = (subspaceName?: GroupSubspaceName) => {
    let url = this.props.urls.groupUrl(this.props.spaceInfo.item.id);
    if (subspaceName) {
      const urlFn = GroupSpace.groupUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.spaceInfo.item.id) || url;
    }
    return url;
  }
}

export function addUsersButton(group: IGroupInfo,
  canSessionUserManageUsers: boolean,
  addSiteUsersToGroup: (group: IGroupInfo) => void): JSX.Element {
  if (canSessionUserManageUsers) {
    const handleClick = () => addSiteUsersToGroup(group);
    return (
      <ButtonWidget
        children={Messages.addUsers()}
        buttonType={ButtonType.Outline}
        handleClick={handleClick}
        tb-test-id={'action-add-users'}
        testId={'action-add-users'}/>
    );
  }
  return null;
}

function getGroupSubspaces(
  angularContext: AngularContext,
  groupDetails: IGetGroupDetailInfoResult,
  contentCountsUpToDate: boolean,
  navigateToAllGroups: () => void,
  reloadSpace: () => void,
  contentCounts: ISpaceInfoTypeItemCount<'group'>,
  telemetry: Telemetry,
  group: IGroupInfo,
  canSessionUserManageUsers: boolean,
  addSiteUsersToGroup: (group: IGroupInfo) => void
): ReadonlyArray<Subspace> {
  return _.compact([
    explorerSubspace({
      name: 'users',
      tabLabel: Messages.members(),
      supportedDisplayModes: ['list'],
      angularContext,
      telemetry: telemetry,
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['users'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      toolbarContentSpecificControls: { beginning: addUsersButton(group, canSessionUserManageUsers, addSiteUsersToGroup)}
    }),
    {
      name: 'details',
      tabLabel: Messages.details(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <DetailsSubspace
          angularContext={angularContext}
          group={groupDetails}
          navigateToAllGroups={navigateToAllGroups}
          reloadSpace={reloadSpace}
        />
      )
    }
  ]);
}

const template = `<div ng-controller="GroupDetailsCtrl" ng-include="'groupDetails.html'"></div>`;

interface DetailsSubspaceProps {
  readonly angularContext: AngularContext;
  readonly group: IGetGroupDetailInfoResult;
  readonly navigateToAllGroups: () => void;
  readonly reloadSpace: () => void;
}

class DetailsSubspace extends React.PureComponent<DetailsSubspaceProps> {

  private childGroupScope: IGroupDetailsCtrl = undefined;
  public componentDidUpdate() {
    if (!_.isUndefined(this.childGroupScope)) {
      this.childGroupScope.groupDetails = this.props.group;
    }
  }

  public render() {
    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IGroupDetailsCtrl;
    this.childGroupScope = childScope;
    childScope.groupDetails = this.props.group;
    childScope.navigateToAllGroups = this.props.navigateToAllGroups;
    childScope.reloadSpace = this.props.reloadSpace;
    return childScope;
  }
}
